import { DetectedCharacter } from './invisibleCharacters';
import {
  detectHomoglyphs,
  analyzeScriptMixing,
  checkNormalizationInstability,
  detectRepeatingInvisiblePattern,
  analyzePositionalPatterns,
  detectDiacriticsAbuse,
  detectFormatCharacterAbuse,
  analyzeSemanticContext,
  detectURLConfusables,
  detectBiDiExploits,
  detectZeroWidthChains,
  detectLanguageSpecificAttacks,
  detectMathSymbolSubstitution,
  detectControlCharacterExploits,
  detectWhitespaceAbuse,
  detectCharacterWidthAbuse,
  detectLookalikeDigits,
  detectQuotationConfusion,
  analyzeFrequency,
  detectDomainSpecificPatterns,
  detectLengthManipulation,
  detectPUACharacters,
  detectFullwidthExploitation,
  detectTagBlockAnomalies,
  analyzeEmojiTokens,
  type HomoglyphMatch,
  type ScriptMixing,
  type NormalizationResult,
  type RepeatingPattern,
  type PositionalPattern,
  type DiacriticsAbuse,
  type FormatAbuse,
  type SemanticContextIssue,
  type URLConfusable,
  type BiDiExploit,
  type ZeroWidthChain,
  type LanguageSpecificIssue,
  type MathSymbolSubstitution,
  type ControlCharExploit,
  type WhitespaceAbuse,
  type CharacterWidthAbuse,
  type LookalikeDigitsAbuse,
  type QuotationConfusion,
  type FrequencyAnalysis,
  type DomainSpecificAbuse,
  type StringLengthAbuse,
  type PUADetection,
  type FullwidthExploitation,
  type TagBlockAnomalies,
  type EmojiTokenAnalysis,
} from './patternDetection';

export type ThreatLevel = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export interface SecurityIssue {
  type: 'control' | 'steganography' | 'suspicious_combination' | 'directional_override'
    | 'homoglyph' | 'script_mixing' | 'normalization' | 'repeating_pattern'
    | 'positional_pattern' | 'diacritics_abuse' | 'format_abuse'
    | 'semantic_context' | 'url_confusable' | 'bidi_exploit' | 'zw_chain'
    | 'language_specific' | 'math_substitution' | 'control_exploit'
    | 'whitespace_abuse' | 'width_anomaly' | 'lookalike_digit' | 'quotation_confusion'
    | 'frequency_anomaly' | 'domain_specific' | 'length_manipulation';
  severity: ThreatLevel;
  description: string;
  positions: number[];
  recommendation: string;
  details?: string;
}

export interface SteganographyPattern {
  type: 'binary_zwsp' | 'zwj_sequence' | 'homoglyph' | 'spacing'
    | 'repeating_invisible' | 'positional';
  confidence: number; // 0-100
  decodedData?: string;
  details: string;
}

export interface SecurityAnalysis {
  threatLevel: ThreatLevel;
  score: number; // 0-100
  issues: SecurityIssue[];
  hasSteganography: boolean;
  steganographyDetails?: SteganographyPattern;
  homoglyphs?: HomoglyphMatch[];
  scriptMixing?: ScriptMixing;
  normalization?: NormalizationResult;
  repeatingPattern?: RepeatingPattern;
  positionalPattern?: PositionalPattern;
  diacriticsAbuse?: DiacriticsAbuse;
  formatAbuse?: FormatAbuse;
  semanticContext?: SemanticContextIssue;
  urlConfusables?: URLConfusable[];
  bidiExploit?: BiDiExploit;
  zwChain?: ZeroWidthChain;
  languageIssues?: LanguageSpecificIssue[];
  mathSubstitutions?: MathSymbolSubstitution[];
  controlExploit?: ControlCharExploit;
  whitespaceAbuse?: WhitespaceAbuse;
  widthAbuse?: CharacterWidthAbuse;
  lookalikeDigits?: LookalikeDigitsAbuse;
  quotationConfusion?: QuotationConfusion;
  frequencyAnalysis?: FrequencyAnalysis;
  domainAbuse?: DomainSpecificAbuse;
  lengthAbuse?: StringLengthAbuse;
  
  // ===== 2024 UNICODE THREAT DETECTION =====
  puaDetection?: PUADetection;
  fullwidthExploitation?: FullwidthExploitation;
  tagBlockAnomalies?: TagBlockAnomalies;
  emojiTokenAnalysis?: EmojiTokenAnalysis;
}

// High-risk control characters that could manipulate rendering or execution
const HIGH_RISK_CONTROL_CHARS = [
  0x0000, // Null
  0x0001, 0x0002, 0x0003, 0x0004, // SOH, STX, ETX, EOT
  0x001B, // ESC
  0x007F, // DEL
  0x009B, // CSI (Control Sequence Introducer)
  0x009D, // OSC (Operating System Command)
];

// Directional override characters (can be used for spoofing)
const DIRECTIONAL_OVERRIDE_CHARS = [
  0x202A, // LRE (Left-to-Right Embedding)
  0x202B, // RLE (Right-to-Left Embedding)
  0x202C, // PDF (Pop Directional Formatting)
  0x202D, // LRO (Left-to-Right Override)
  0x202E, // RLO (Right-to-Left Override)
  0x2066, // LRI (Left-to-Right Isolate)
  0x2067, // RLI (Right-to-Left Isolate)
  0x2068, // FSI (First Strong Isolate)
  0x2069, // PDI (Pop Directional Isolate)
];

// Zero-width characters commonly used in steganography
const ZERO_WIDTH_CHARS = [
  0x200B, // ZWSP (Zero Width Space)
  0x200C, // ZWNJ (Zero Width Non-Joiner)
  0x200D, // ZWJ (Zero Width Joiner)
  0xFEFF, // ZWNBSP (Zero Width No-Break Space / BOM)
];

/**
 * Analyze text for steganography patterns
 */
export function detectSteganography(text: string): SteganographyPattern | null {
  // 1. Check for binary encoding using ZWSP/ZWNJ
  const binaryPattern = detectBinaryEncoding(text);
  if (binaryPattern) {
    return binaryPattern;
  }

  // 2. Check for ZWJ sequences (invisible character chains)
  const zwjPattern = detectZWJSequence(text);
  if (zwjPattern) {
    return zwjPattern;
  }

  // 3. Check for spacing-based steganography
  const spacingPattern = detectSpacingPattern(text);
  if (spacingPattern) {
    return spacingPattern;
  }

  return null;
}

/**
 * Detect binary encoding using ZWSP (0) and ZWNJ (1)
 */
function detectBinaryEncoding(text: string): SteganographyPattern | null {
  let binaryString = '';
  
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code === 0x200B) {
      binaryString += '0';
    } else if (code === 0x200C) {
      binaryString += '1';
    }
  }
  
  // Need at least 8 bits to be considered a pattern
  if (binaryString.length < 8) {
    return null;
  }

  // Calculate confidence based on pattern regularity
  const confidence = Math.min(100, 50 + (binaryString.length / text.length) * 100);

  // Try to decode as ASCII
  let decodedData = '';
  for (let i = 0; i < binaryString.length; i += 8) {
    const byte = binaryString.substr(i, 8);
    if (byte.length === 8) {
      const charCode = parseInt(byte, 2);
      // Only include printable ASCII
      if (charCode >= 32 && charCode <= 126) {
        decodedData += String.fromCharCode(charCode);
      } else {
        decodedData += '�';
      }
    }
  }

  return {
    type: 'binary_zwsp',
    confidence: Math.round(confidence),
    decodedData: decodedData || undefined,
    details: `Binary pattern detected: ${binaryString.length} bits (${Math.floor(binaryString.length / 8)} bytes). ZWSP=0, ZWNJ=1.`,
  };
}

/**
 * Detect suspicious ZWJ (Zero Width Joiner) sequences
 */
function detectZWJSequence(text: string): SteganographyPattern | null {
  const zwjPositions: number[] = [];
  
  for (let i = 0; i < text.length; i++) {
    if (text.charCodeAt(i) === 0x200D) {
      zwjPositions.push(i);
    }
  }

  // Multiple ZWJs in short text is suspicious
  if (zwjPositions.length >= 3) {
    const density = zwjPositions.length / text.length;
    const confidence = Math.min(100, 40 + density * 1000);

    return {
      type: 'zwj_sequence',
      confidence: Math.round(confidence),
      details: `${zwjPositions.length} Zero Width Joiners detected. High density may indicate hidden data encoding.`,
    };
  }

  return null;
}

/**
 * Detect spacing-based steganography (unusual space character patterns)
 */
function detectSpacingPattern(text: string): SteganographyPattern | null {
  const spaceTypes = new Set<number>();

  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    // Check for various space characters
    if (
      code === 0x0020 || // Regular space
      code === 0x00A0 || // No-break space
      code === 0x2000 || code === 0x2001 || code === 0x2002 || // En/Em spaces
      code === 0x2003 || code === 0x2004 || code === 0x2005 ||
      code === 0x2006 || code === 0x2007 || code === 0x2008 ||
      code === 0x2009 || code === 0x200A || // Thin/Hair spaces
      code === 0x202F || code === 0x205F || code === 0x3000 // Other spaces
    ) {
      spaceTypes.add(code);
    }
  }

  // If using 3+ different types of spaces, could be steganography
  if (spaceTypes.size >= 3) {
    const confidence = Math.min(100, 30 + spaceTypes.size * 15);

    return {
      type: 'spacing',
      confidence: Math.round(confidence),
      details: `${spaceTypes.size} different space character types detected. Mixed space usage may encode hidden data.`,
    };
  }

  return null;
}

/**
 * Detect suspicious character combinations
 */
function detectSuspiciousCombinations(detectedChars: DetectedCharacter[]): SecurityIssue[] {
  const issues: SecurityIssue[] = [];

  // Group by character type
  const charsByType = new Map<number, DetectedCharacter[]>();
  detectedChars.forEach(detected => {
    const code = detected.character.code;
    if (!charsByType.has(code)) {
      charsByType.set(code, []);
    }
    charsByType.get(code)!.push(detected);
  });

  // Check for directional override abuse
  const directionalChars = detectedChars.filter(d => 
    DIRECTIONAL_OVERRIDE_CHARS.includes(d.character.code)
  );

  if (directionalChars.length >= 2) {
    issues.push({
      type: 'directional_override',
      severity: directionalChars.length >= 4 ? 'HIGH' : 'MEDIUM',
      description: `${directionalChars.length} directional override characters detected`,
      positions: directionalChars.map(c => c.position),
      recommendation: 'Directional override characters can be used to spoof text direction and create visual deception. Remove them unless necessary for legitimate RTL/LTR text.',
    });
  }

  // Check for excessive zero-width characters
  const zeroWidthChars = detectedChars.filter(d => 
    ZERO_WIDTH_CHARS.includes(d.character.code)
  );

  if (zeroWidthChars.length >= 10) {
    issues.push({
      type: 'suspicious_combination',
      severity: zeroWidthChars.length >= 20 ? 'HIGH' : 'MEDIUM',
      description: `${zeroWidthChars.length} zero-width characters detected`,
      positions: zeroWidthChars.map(c => c.position),
      recommendation: 'Excessive zero-width characters may indicate steganography or text manipulation. Review and remove unnecessary instances.',
    });
  }

  // Check for control character sequences
  const controlChars = detectedChars.filter(d => 
    d.character.category === 'control' && HIGH_RISK_CONTROL_CHARS.includes(d.character.code)
  );

  if (controlChars.length > 0) {
    issues.push({
      type: 'control',
      severity: controlChars.length >= 3 ? 'CRITICAL' : 'HIGH',
      description: `${controlChars.length} high-risk control characters detected`,
      positions: controlChars.map(c => c.position),
      recommendation: 'High-risk control characters can manipulate terminal output or system behavior. Remove them immediately unless required for specific technical purposes.',
    });
  }

  return issues;
}

/**
 * Calculate risk score based on detected issues
 */
function calculateRiskScore(
  detectedChars: DetectedCharacter[],
  issues: SecurityIssue[],
  hasSteganography: boolean,
  textLength: number
): number {
  let score = 0;

  // Base score from character count and density
  const density = textLength > 0 ? detectedChars.length / textLength : 0;
  score += Math.min(30, density * 100); // Up to 30 points for density

  // Points for different character categories
  const categories = new Set(detectedChars.map(d => d.character.category));
  if (categories.has('control')) score += 20;
  if (categories.has('zero-width')) score += 15;
  if (categories.has('directional')) score += 15;

  // Points for issues
  issues.forEach(issue => {
    switch (issue.severity) {
      case 'LOW': score += 5; break;
      case 'MEDIUM': score += 15; break;
      case 'HIGH': score += 25; break;
      case 'CRITICAL': score += 40; break;
    }
  });

  // Points for steganography
  if (hasSteganography) {
    score += 30;
  }

  return Math.min(100, Math.round(score));
}

/**
 * Determine threat level based on score and issues
 */
function determineThreatLevel(
  score: number,
  issues: SecurityIssue[]
): ThreatLevel {
  // Critical if any critical issues
  if (issues.some(i => i.severity === 'CRITICAL')) {
    return 'CRITICAL';
  }

  // Otherwise base on score
  if (score >= 75) return 'HIGH';
  if (score >= 50) return 'MEDIUM';
  if (score >= 25) return 'LOW';
  return 'LOW';
}

/**
 * Main function to perform comprehensive security analysis
 */
export function analyzeSecurityRisks(
  text: string,
  detectedChars: DetectedCharacter[]
): SecurityAnalysis {
  const issues: SecurityIssue[] = [];

  // 1. Detect steganography
  const steganographyPattern = detectSteganography(text);
  if (steganographyPattern) {
    issues.push({
      type: 'steganography',
      severity: steganographyPattern.confidence >= 70 ? 'HIGH' : 'MEDIUM',
      description: `Potential steganography detected (${steganographyPattern.confidence}% confidence)`,
      positions: [],
      recommendation: `${steganographyPattern.details} This may be used to hide data or create invisible watermarks. Review the decoded content carefully.`,
    });
  }

  // 2. Detect suspicious combinations
  const combinationIssues = detectSuspiciousCombinations(detectedChars);
  issues.push(...combinationIssues);

  // 3. Detect homoglyphs
  const homoglyphs = detectHomoglyphs(text);
  if (homoglyphs.length > 0) {
    const avgConfidence = Math.round(homoglyphs.reduce((sum, h) => sum + h.confidence, 0) / homoglyphs.length);
    issues.push({
      type: 'homoglyph',
      severity: homoglyphs.length >= 5 ? 'HIGH' : homoglyphs.length >= 2 ? 'MEDIUM' : 'LOW',
      description: `${homoglyphs.length} homoglyph/confusable character${homoglyphs.length > 1 ? 's' : ''} detected`,
      positions: homoglyphs.map(h => h.position),
      recommendation: 'Confusable characters can be used for phishing, domain spoofing, or bypassing text filters. Verify text authenticity and consider replacing with standard Latin characters.',
      details: `Average confidence: ${avgConfidence}%. Scripts detected: ${[...new Set(homoglyphs.map(h => h.script))].join(', ')}`,
    });
  }

  // 4. Analyze script mixing
  const scriptMixing = analyzeScriptMixing(text);
  if (scriptMixing.isSuspicious) {
    issues.push({
      type: 'script_mixing',
      severity: scriptMixing.mixedWords.length >= 3 ? 'HIGH' : 'MEDIUM',
      description: `Suspicious script mixing detected (${scriptMixing.mixedWords.length} mixed word${scriptMixing.mixedWords.length > 1 ? 's' : ''})`,
      positions: scriptMixing.mixedWords.map(w => w.position),
      recommendation: 'Mixing different Unicode scripts within words is unusual and may indicate spoofing attempts or filter evasion. Review words for authenticity.',
      details: `Scripts found: ${scriptMixing.scripts.join(', ')}. Mixed words: ${scriptMixing.mixedWords.map(w => w.word).join(', ')}`,
    });
  }

  // 5. Check normalization instability
  const normalization = checkNormalizationInstability(text);
  if (normalization.isUnstable) {
    issues.push({
      type: 'normalization',
      severity: normalization.changesDetected.length >= 3 ? 'MEDIUM' : 'LOW',
      description: `Unicode normalization instability detected (${normalization.changesDetected.length} issue${normalization.changesDetected.length > 1 ? 's' : ''})`,
      positions: [],
      recommendation: 'Text changes during Unicode normalization, which can cause validation bypasses or unexpected behavior. Consider normalizing text to NFC form.',
      details: normalization.changesDetected.join('; '),
    });
  }

  // 6. Detect repeating invisible patterns
  const repeatingPattern = detectRepeatingInvisiblePattern(text, detectedChars);
  if (repeatingPattern) {
    issues.push({
      type: 'repeating_pattern',
      severity: repeatingPattern.confidence >= 80 ? 'HIGH' : 'MEDIUM',
      description: `Repeating invisible character pattern detected (${repeatingPattern.confidence}% confidence)`,
      positions: repeatingPattern.positions,
      recommendation: 'Regular patterns of invisible characters often indicate steganography or hidden data encoding. This may be used for covert communication or tracking.',
      details: repeatingPattern.description,
    });
  }

  // 7. Analyze positional patterns
  const positionalPattern = analyzePositionalPatterns(detectedChars, text.length);
  if (positionalPattern && positionalPattern.distribution !== 'uniform') {
    issues.push({
      type: 'positional_pattern',
      severity: positionalPattern.distribution === 'start' || positionalPattern.distribution === 'end' ? 'MEDIUM' : 'LOW',
      description: `Non-uniform distribution of invisible characters detected (${positionalPattern.distribution})`,
      positions: positionalPattern.clusterPositions,
      recommendation: 'Clustered invisible characters at specific positions may indicate metadata encoding, tracking markers, or intentional manipulation.',
      details: `Distribution type: ${positionalPattern.distribution}. Start clustering: ${positionalPattern.startClustering}, End clustering: ${positionalPattern.endClustering}`,
    });
  }

  // 8. Detect diacritics abuse
  const diacriticsAbuse = detectDiacriticsAbuse(text);
  if (diacriticsAbuse.hasAbuse) {
    issues.push({
      type: 'diacritics_abuse',
      severity: diacriticsAbuse.maxDiacriticsOnChar >= 10 ? 'HIGH' : diacriticsAbuse.maxDiacriticsOnChar >= 5 ? 'MEDIUM' : 'LOW',
      description: `Excessive combining diacritics detected (max ${diacriticsAbuse.maxDiacriticsOnChar} on single character)`,
      positions: diacriticsAbuse.affectedPositions.map(p => p.position),
      recommendation: 'Excessive diacritical marks (Zalgo text) can cause rendering issues, buffer overflows, or be used to obfuscate text. Remove unnecessary combining marks.',
      details: `${diacriticsAbuse.affectedPositions.length} character${diacriticsAbuse.affectedPositions.length > 1 ? 's' : ''} affected`,
    });
  }

  // 9. Detect format character abuse
  const formatAbuse = detectFormatCharacterAbuse(text);
  if (formatAbuse.hasAbuse) {
    issues.push({
      type: 'format_abuse',
      severity: formatAbuse.bomCount >= 2 ? 'HIGH' : 'MEDIUM',
      description: `Format character abuse detected (${formatAbuse.positions.length} occurrence${formatAbuse.positions.length > 1 ? 's' : ''})`,
      positions: formatAbuse.positions.map(p => p.position),
      recommendation: 'Format characters in unexpected positions can manipulate text parsing, bypass validation, or cause rendering issues. Remove format characters unless required.',
      details: `BOMs: ${formatAbuse.bomCount}, Soft hyphens: ${formatAbuse.softHyphenCount}, Separators: ${formatAbuse.separatorCount}`,
    });
  }

  // 10. Analyze semantic context
  const semanticContext = analyzeSemanticContext(text, detectedChars);
  if (semanticContext) {
    issues.push({
      type: 'semantic_context',
      severity: semanticContext.affectedWords.length >= 3 ? 'HIGH' : 'MEDIUM',
      description: semanticContext.description,
      positions: semanticContext.affectedWords.flatMap(w => w.splitPositions),
      recommendation: 'Invisible characters within words are often used for filter evasion or content manipulation. Review affected words and remove invisible characters.',
      details: `Affected words: ${semanticContext.affectedWords.map(w => w.word).join(', ')}`,
    });
  }

  // 11. Detect URL confusables
  const urlConfusables = detectURLConfusables(text);
  if (urlConfusables.length > 0) {
    urlConfusables.forEach(urlConf => {
      const highestSeverity = urlConf.issues.some(i => i.type === 'punycode' || i.type === 'tld_spoof') ? 'HIGH' : 'MEDIUM';
      issues.push({
        type: 'url_confusable',
        severity: highestSeverity,
        description: `Suspicious URL detected: ${urlConf.url}`,
        positions: [urlConf.position],
        recommendation: 'This URL contains characteristics commonly used in phishing attacks. Verify the domain authenticity before accessing.',
        details: urlConf.issues.map(i => i.details).join('; '),
      });
    });
  }

  // 12. Detect BiDi exploits
  const bidiExploit = detectBiDiExploits(text);
  if (bidiExploit) {
    issues.push({
      type: 'bidi_exploit',
      severity: bidiExploit.risk,
      description: `Bidirectional text exploit detected (${bidiExploit.type})`,
      positions: bidiExploit.positions,
      recommendation: bidiExploit.risk === 'CRITICAL' 
        ? 'CRITICAL: RLO/LRO characters detected. These can be used for filename spoofing (e.g., making .exe appear as .jpg). Remove immediately unless required for legitimate RTL text.' 
        : 'Bidirectional text controls detected. Review for potential spoofing or trojan source attacks.',
      details: `Displayed text may differ from actual content. Positions: ${bidiExploit.positions.length}`,
    });
  }

  // 13. Detect zero-width chains
  const zwChain = detectZeroWidthChains(text, detectedChars);
  if (zwChain) {
    issues.push({
      type: 'zw_chain',
      severity: zwChain.chainLength >= 5 ? 'HIGH' : zwChain.isStackedInvisible ? 'HIGH' : 'MEDIUM',
      description: `Zero-width character chain detected: ${zwChain.pattern}`,
      positions: zwChain.positions,
      recommendation: 'Chains of zero-width characters are often used for advanced steganography or text manipulation. Review and remove unless necessary for legitimate text rendering (e.g., emojis).',
      details: `Chain length: ${zwChain.chainLength}. ${zwChain.isStackedInvisible ? 'Contains stacked invisible characters.' : ''}`,
    });
  }

  // 14. Detect language-specific attacks
  const languageIssues = detectLanguageSpecificAttacks(text);
  if (languageIssues.length > 0) {
    languageIssues.forEach(langIssue => {
      issues.push({
        type: 'language_specific',
        severity: langIssue.positions.length >= 3 ? 'MEDIUM' : 'LOW',
        description: `${langIssue.language} character attack: ${langIssue.description}`,
        positions: langIssue.positions,
        recommendation: 'Language-specific characters with unusual case-folding or normalization behavior can bypass security filters. Normalize text or restrict character sets.',
        details: `Issue type: ${langIssue.issueType}. Affected positions: ${langIssue.positions.length}`,
      });
    });
  }

  // 15. Detect math symbol substitution
  const mathSubstitutions = detectMathSymbolSubstitution(text);
  if (mathSubstitutions.length > 0) {
    mathSubstitutions.forEach(mathSub => {
      issues.push({
        type: 'math_substitution',
        severity: mathSub.count >= 10 ? 'MEDIUM' : 'LOW',
        description: `Mathematical symbol substitution detected (${mathSub.category}): ${mathSub.count} character${mathSub.count > 1 ? 's' : ''}`,
        positions: mathSub.positions.map(p => p.position),
        recommendation: 'Mathematical Unicode variants can bypass text filters and create visual confusion. Consider replacing with standard ASCII characters.',
        details: `Category: ${mathSub.category}. Substitutions: ${mathSub.positions.map(p => `${p.substitute}→${p.original}`).slice(0, 5).join(', ')}${mathSub.count > 5 ? '...' : ''}`,
      });
    });
  }

  // 16. Detect control character exploits
  const controlExploit = detectControlCharacterExploits(text);
  if (controlExploit) {
    issues.push({
      type: 'control_exploit',
      severity: controlExploit.risk,
      description: `Control character exploit detected: ${controlExploit.type}`,
      positions: controlExploit.sequences.map(s => s.position),
      recommendation: controlExploit.risk === 'CRITICAL'
        ? 'CRITICAL: Terminal control sequences detected. These can manipulate terminal output, hide malicious content, or execute commands. Remove immediately.'
        : 'Control characters detected that may cause unexpected behavior. Review and remove unless required for specific formatting.',
      details: `${controlExploit.sequences.length} sequence${controlExploit.sequences.length > 1 ? 's' : ''} detected: ${controlExploit.sequences.slice(0, 3).map(s => s.description).join('; ')}${controlExploit.sequences.length > 3 ? '...' : ''}`,
    });
  }

  // 17. Detect whitespace abuse
  const whitespaceAbuse = detectWhitespaceAbuse(text);
  if (whitespaceAbuse.hasAbuse) {
    whitespaceAbuse.issues.forEach(issue => {
      const severity: ThreatLevel = issue.type === 'mixed_types' ? 'MEDIUM' : issue.type === 'trailing' ? 'LOW' : 'LOW';
      issues.push({
        type: 'whitespace_abuse',
        severity,
        description: `Whitespace abuse detected: ${issue.description}`,
        positions: issue.positions.slice(0, 50), // Limit positions
        recommendation: issue.type === 'mixed_types' 
          ? 'Multiple space types detected. This can indicate steganography or manipulation attempts. Normalize to single space type.'
          : issue.type === 'trailing'
          ? 'Trailing whitespace detected. May be used for duplicate account creation or data manipulation.'
          : 'Unusual whitespace patterns detected. Review for potential manipulation or hidden data.',
        details: issue.spaceTypes.length > 0 ? `Space types: ${issue.spaceTypes.join(', ')}` : undefined,
      });
    });
  }

  // 18. Detect character width anomalies
  const widthAbuse = detectCharacterWidthAbuse(text);
  if (widthAbuse.hasAbuse) {
    issues.push({
      type: 'width_anomaly',
      severity: widthAbuse.mixedWidths ? 'HIGH' : 'MEDIUM',
      description: `${widthAbuse.anomalies.length} full-width/half-width character${widthAbuse.anomalies.length > 1 ? 's' : ''} detected`,
      positions: widthAbuse.anomalies.map(a => a.position),
      recommendation: 'Full-width characters can bypass filters, create duplicate accounts, or spoof usernames. Replace with standard ASCII characters.',
      details: `${widthAbuse.mixedWidths ? 'Mixed with normal ASCII. ' : ''}Examples: ${widthAbuse.anomalies.slice(0, 5).map(a => `${a.character} (${a.width})`).join(', ')}${widthAbuse.anomalies.length > 5 ? '...' : ''}`,
    });
  }

  // 19. Detect look-alike digits
  const lookalikeDigits = detectLookalikeDigits(text);
  if (lookalikeDigits.hasAbuse) {
    issues.push({
      type: 'lookalike_digit',
      severity: lookalikeDigits.digits.length >= 5 ? 'MEDIUM' : 'LOW',
      description: `${lookalikeDigits.digits.length} look-alike digit${lookalikeDigits.digits.length > 1 ? 's' : ''} detected`,
      positions: lookalikeDigits.digits.map(d => d.position),
      recommendation: 'Unicode digit variants can bypass validation, create confusion, or be used in financial/authentication attacks. Replace with standard digits.',
      details: `Types: ${[...new Set(lookalikeDigits.digits.map(d => d.digitType))].join(', ')}. Examples: ${lookalikeDigits.digits.slice(0, 5).map(d => `${d.character} (${d.digitType})`).join(', ')}${lookalikeDigits.digits.length > 5 ? '...' : ''}`,
    });
  }

  // 20. Detect quotation mark confusion
  const quotationConfusion = detectQuotationConfusion(text);
  if (quotationConfusion.hasConfusion) {
    // Use confidence score to determine severity
    let severity: ThreatLevel = 'LOW';
    let description = `${quotationConfusion.anomalies.length} non-standard quotation mark${quotationConfusion.anomalies.length > 1 ? 's' : ''} detected`;
    let recommendation = 'Non-standard quotation marks can bypass filters or cause parsing issues. Replace with standard ASCII quotes.';
    
    if (quotationConfusion.potentialSQLInjection && quotationConfusion.sqlInjectionConfidence) {
      const confidence = quotationConfusion.sqlInjectionConfidence;
      
      // Determine severity based on confidence
      if (confidence >= 75) {
        severity = 'HIGH';
      } else if (confidence >= 50) {
        severity = 'MEDIUM';
      } else {
        severity = 'LOW';
      }
      
      description += ` (potential SQL injection - ${confidence}% confidence)`;
      recommendation = `${severity === 'HIGH' ? 'CRITICAL: ' : ''}Non-standard quotes detected with SQL keywords (confidence: ${confidence}%). This may be an SQL injection attempt using typographic quotes to bypass filters. Review the context carefully.`;
    } else if (quotationConfusion.anomalies.length > 10) {
      // Many typographic quotes without SQL concerns
      severity = 'MEDIUM';
    }
    
    issues.push({
      type: 'quotation_confusion',
      severity,
      description,
      positions: quotationConfusion.anomalies.map(a => a.position),
      recommendation,
      details: `Types: ${[...new Set(quotationConfusion.anomalies.map(a => a.type))].join(', ')}. Examples: ${quotationConfusion.anomalies.slice(0, 5).map(a => `${a.character} (${a.type})`).join(', ')}${quotationConfusion.anomalies.length > 5 ? '...' : ''}`,
    });
  }

  // 21. Analyze frequency & detect encoding
  const frequencyAnalysis = analyzeFrequency(text);
  if (frequencyAnalysis.hasAnomaly) {
    frequencyAnalysis.suspiciousPatterns.forEach(pattern => {
      const severityMap: Record<typeof pattern.type, ThreatLevel> = {
        'base64': 'MEDIUM',
        'high_entropy': 'HIGH',
        'uniform_distribution': 'MEDIUM',
        'non_textual': 'LOW',
      };
      
      // Build detailed statistics for better context
      const statsDetails = [
        `Text length: ${text.length} chars`,
        `Unique characters: ${frequencyAnalysis.characterDistribution.length}`,
        `Entropy: ${frequencyAnalysis.entropy.toFixed(2)} bits`,
        `Confidence: ${pattern.confidence}%`
      ];
      
      if (pattern.sample) {
        statsDetails.push(`Sample: ${pattern.sample}`);
      }
      
      issues.push({
        type: 'frequency_anomaly',
        severity: severityMap[pattern.type],
        description: `Frequency anomaly: ${pattern.description}`,
        positions: [], // Statistical analysis applies to entire text
        recommendation: pattern.type === 'base64' 
          ? 'Base64 encoding detected. Decode and analyze the content for security threats.'
          : pattern.type === 'high_entropy'
          ? 'High entropy suggests encrypted or compressed data. Investigate the source and purpose.'
          : 'Unusual character distribution detected. This applies to the entire text as it\'s a statistical property. May indicate encoded, encrypted, or non-natural text.',
        details: statsDetails.join(' | '),
      });
    });
  }

  // 22. Detect domain-specific pattern abuse
  const domainAbuse = detectDomainSpecificPatterns(text);
  if (domainAbuse.hasAbuse) {
    domainAbuse.patterns.forEach(pattern => {
      const severityMap: Record<typeof pattern.type, ThreatLevel> = {
        'email': 'MEDIUM',
        'ip': 'HIGH',
        'phone': 'LOW',
        'credit_card': 'MEDIUM',
      };
      
      issues.push({
        type: 'domain_specific',
        severity: severityMap[pattern.type],
        description: `${pattern.type.replace('_', ' ').toUpperCase()} with suspicious characters: ${pattern.issues.length} issue${pattern.issues.length > 1 ? 's' : ''}`,
        positions: [pattern.position],
        recommendation: pattern.type === 'email'
          ? 'Email contains unusual characters (full-width or Cyrillic). Verify domain authenticity to prevent phishing.'
          : pattern.type === 'ip'
          ? 'IP address uses non-standard separators. This may be an attempt to bypass URL filters or logging.'
          : pattern.type === 'phone'
          ? 'Phone number uses Unicode digit variants. Normalize to standard digits for validation.'
          : 'Credit card number uses non-standard digits. This may bypass payment validation.',
        details: `Value: ${pattern.value}. Issues: ${pattern.issues.join('; ')}${pattern.normalized ? `. Normalized: ${pattern.normalized}` : ''}`,
      });
    });
  }

  // 23. Detect string length manipulation
  const lengthAbuse = detectLengthManipulation(text);
  if (lengthAbuse.hasAbuse) {
    issues.push({
      type: 'length_manipulation',
      severity: lengthAbuse.ratio > 2 ? 'MEDIUM' : 'LOW',
      description: `String length manipulation detected: ${lengthAbuse.discrepancies.length} grapheme discrepancies`,
      positions: lengthAbuse.discrepancies.map(d => d.position),
      recommendation: 'Text contains characters that display as single units but store as multiple code points. This can bypass length validation and create security vulnerabilities.',
      details: `Ratio: ${lengthAbuse.ratio.toFixed(2)}x (${lengthAbuse.totalCodePoints} code points / ${lengthAbuse.totalGraphemes} graphemes). Examples: ${lengthAbuse.discrepancies.slice(0, 3).map(d => d.description).join('; ')}${lengthAbuse.discrepancies.length > 3 ? '...' : ''}`,
    });
  }

  // ===== 2024 UNICODE THREAT DETECTION =====

  // 24. Detect Private Use Area (PUA) characters
  const puaDetection = detectPUACharacters(text);
  if (puaDetection.hasPUA) {
    issues.push({
      type: 'steganography', // Use steganography type since PUA is primarily used for hidden data
      severity: puaDetection.riskLevel,
      description: `Private Use Area (PUA) characters detected: ${puaDetection.totalPUACount} chars (${(puaDetection.density * 100).toFixed(2)}% density)`,
      positions: puaDetection.positions,
      recommendation: puaDetection.suggestedCategory === 'icon_font' 
        ? 'Icon font characters detected. Ensure proper font context is available for rendering.'
        : puaDetection.suggestedCategory === 'steganography'
        ? 'High density of PUA characters suggests hidden data encoding or steganography. Investigate source and purpose of these characters.'
        : 'PUA characters detected. These have no standardized meaning and may indicate custom data encoding.',
      details: `Category: ${puaDetection.suggestedCategory}. Ranges: ${puaDetection.ranges.map(r => `${r.range} (${r.count} chars, ${r.percentage.toFixed(1)}%)`).join('; ')}. Patterns: ${puaDetection.patterns.map(p => `${p.type}: ${p.likelihood}`).join(', ')}`,
    });
  }

  // 25. Detect Fullwidth exploitation (CVE-2024-4577, etc.)
  const fullwidthExploit = detectFullwidthExploitation(text);
  if (fullwidthExploit.hasFullwidth) {
    issues.push({
      type: 'width_anomaly',
      severity: fullwidthExploit.riskLevel,
      description: `Fullwidth character exploitation detected: ${fullwidthExploit.dangerousChars.length} dangerous char${fullwidthExploit.dangerousChars.length > 1 ? 's' : ''}`,
      positions: fullwidthExploit.dangerousChars.map(d => d.position),
      recommendation: fullwidthExploit.cveRelated !== 'none'
        ? `CVE vulnerability detected (${fullwidthExploit.cveRelated}). Fullwidth characters may bypass validation and normalize to dangerous ASCII characters. Always normalize BEFORE validation.`
        : 'Fullwidth characters detected. These may bypass input validation if normalized after validation checks. Ensure proper validation order.',
      details: `CVE: ${fullwidthExploit.cveRelated}. Chars: ${fullwidthExploit.dangerousChars.map(d => `${d.char}(U+${d.code}) → '${d.normalizesTo}'`).join(', ')}`,
    });
  }

  // 26. Detect Tag Block anomalies (emoji attack vectors, steganography)
  const tagBlockAnomalies = detectTagBlockAnomalies(text);
  if (tagBlockAnomalies.hasTagBlockChars) {
    issues.push({
      type: 'steganography', // Tag blocks are primarily used for hidden data
      severity: tagBlockAnomalies.riskLevel,
      description: `Tag Block characters (U+E0000–U+E007F) detected: ${tagBlockAnomalies.count} chars`,
      positions: tagBlockAnomalies.positions,
      recommendation: tagBlockAnomalies.inEmojiContext
        ? 'Tag Block characters are legitimately used in emoji flag sequences. Verify this is the intended context.'
        : tagBlockAnomalies.suspiciousPatterns
        ? '2024 LLM Attack Vector: Tag Block characters can be used to smuggle hidden commands in LLM prompts or bypass AI safety filters. Investigate source immediately.'
        : 'Tag Block characters outside emoji context are suspicious. They may be used for data hiding, fingerprinting, or LLM attacks.',
      details: `In emoji context: ${tagBlockAnomalies.inEmojiContext}. Suspicious patterns: ${tagBlockAnomalies.suspiciousPatterns}. 2024 Threat: Tag character smuggling in LLMs`,
    });
  }

  // 27. Analyze Emoji token expansion (2024 LLM attacks)
  const emojiAnalysis = analyzeEmojiTokens(text);
  if (emojiAnalysis.hasComplexEmoji) {
    issues.push({
      type: 'steganography', // Token expansion can hide data
      severity: emojiAnalysis.riskLevel,
      description: `Complex emoji sequences detected: ${emojiAnalysis.emojiSequences.length} sequences (${emojiAnalysis.estimatedTokenCount} estimated tokens)`,
      positions: emojiAnalysis.emojiSequences.map(e => e.position),
      recommendation: emojiAnalysis.tokenExpansionRisk
        ? '2024 LLM Attack: Emoji token expansion detected. Strategic emoji insertion can bypass AI safety filters by causing unexpected tokenization (Wei et al., 2024). This may be used to bypass LLM content filtering.'
        : 'Complex emoji sequences detected. These may cause unexpected tokenization in LLM models or be used for steganography.',
      details: `Complex sequences: ${emojiAnalysis.emojiSequences.map(e => `${e.sequence} (${e.componentCount} components)`).join('; ')}. Token expansion risk: ${emojiAnalysis.tokenExpansionRisk}`,
    });
  }

  // Calculate risk score with new patterns
  const score = calculateRiskScore(
    detectedChars,
    issues,
    steganographyPattern !== null,
    text.length
  );

  // Determine threat level
  const threatLevel = determineThreatLevel(score, issues);

  return {
    threatLevel,
    score,
    issues,
    hasSteganography: steganographyPattern !== null || (repeatingPattern?.confidence ?? 0) >= 70 || (zwChain?.chainLength ?? 0) >= 5,
    steganographyDetails: steganographyPattern || undefined,
    homoglyphs: homoglyphs.length > 0 ? homoglyphs : undefined,
    scriptMixing: scriptMixing.isSuspicious ? scriptMixing : undefined,
    normalization: normalization.isUnstable ? normalization : undefined,
    repeatingPattern: repeatingPattern || undefined,
    positionalPattern: positionalPattern || undefined,
    diacriticsAbuse: diacriticsAbuse.hasAbuse ? diacriticsAbuse : undefined,
    formatAbuse: formatAbuse.hasAbuse ? formatAbuse : undefined,
    semanticContext: semanticContext || undefined,
    urlConfusables: urlConfusables.length > 0 ? urlConfusables : undefined,
    bidiExploit: bidiExploit || undefined,
    zwChain: zwChain || undefined,
    languageIssues: languageIssues.length > 0 ? languageIssues : undefined,
    mathSubstitutions: mathSubstitutions.length > 0 ? mathSubstitutions : undefined,
    controlExploit: controlExploit || undefined,
    whitespaceAbuse: whitespaceAbuse.hasAbuse ? whitespaceAbuse : undefined,
    widthAbuse: widthAbuse.hasAbuse ? widthAbuse : undefined,
    lookalikeDigits: lookalikeDigits.hasAbuse ? lookalikeDigits : undefined,
    quotationConfusion: quotationConfusion.hasConfusion ? quotationConfusion : undefined,
    frequencyAnalysis: frequencyAnalysis.hasAnomaly ? frequencyAnalysis : undefined,
    domainAbuse: domainAbuse.hasAbuse ? domainAbuse : undefined,
    lengthAbuse: lengthAbuse.hasAbuse ? lengthAbuse : undefined,
    
    // ===== 2024 UNICODE THREAT DETECTION RESULTS =====
    puaDetection: puaDetection.hasPUA ? puaDetection : undefined,
    fullwidthExploitation: fullwidthExploit.hasFullwidth ? fullwidthExploit : undefined,
    tagBlockAnomalies: tagBlockAnomalies.hasTagBlockChars ? tagBlockAnomalies : undefined,
    emojiTokenAnalysis: emojiAnalysis.hasComplexEmoji ? emojiAnalysis : undefined,
  };
}

